// © 2026 KaraokeClubs. All rights reserved.

"use client";

import { motion } from "framer-motion";
import { t } from "@/lib/i18n";
import type { HeroSummary } from "@/lib/venue-helpers";
import type { Locale } from "@/lib/types";

interface HeroAnswerProps {
  summary: HeroSummary;
  locale: Locale;
  compact?: boolean;
}

/**
 * The headline answer to "where's karaoke tonight and when does it start?"
 * States the answer in one line so the user doesn't have to scan the list.
 */
export default function HeroAnswer({ summary, locale, compact }: HeroAnswerProps) {
  const { openNow, tonight, earliestTonight, nextVenue } = summary;

  // Decide the dominant message + accent
  let headline: string;
  let detail: string | null = null;
  let accent: "live" | "tonight" | "quiet";

  if (openNow > 0) {
    accent = "live";
    headline = openNow === 1
      ? t(locale, "hero_one_open")
      : t(locale, "hero_open_now").replace("{count}", String(openNow));
    if (tonight > openNow && earliestTonight) {
      detail = t(locale, "hero_earliest").replace("{time}", earliestTonight);
    }
  } else if (tonight > 0) {
    accent = "tonight";
    headline = tonight === 1
      ? t(locale, "hero_one_tonight")
      : t(locale, "hero_tonight").replace("{count}", String(tonight));
    detail = nextVenue
      ? t(locale, "hero_next_up").replace("{name}", nextVenue.name).replace("{time}", nextVenue.opensAt)
      : earliestTonight
        ? t(locale, "hero_earliest").replace("{time}", earliestTonight)
        : null;
  } else {
    accent = "quiet";
    headline = t(locale, "hero_nothing_tonight");
    detail = t(locale, "hero_check_back");
  }

  const dot =
    accent === "live" ? "bg-emerald-400"
    : accent === "tonight" ? "bg-violet-400"
    : "bg-zinc-500";
  const ring =
    accent === "live" ? "from-emerald-500/[0.12] to-emerald-500/[0.02] border-emerald-500/20"
    : accent === "tonight" ? "from-violet-500/[0.12] to-fuchsia-500/[0.03] border-violet-500/20"
    : "from-white/[0.04] to-transparent border-white/[0.06]";
  const headlineCl =
    accent === "live" ? "text-emerald-300"
    : accent === "tonight" ? "text-violet-200"
    : "text-zinc-300";

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className={`relative overflow-hidden rounded-2xl border bg-gradient-to-br ${ring} ${compact ? "px-4 py-3" : "px-4 py-3.5"}`}
    >
      <div className="flex items-center gap-2.5">
        <span className="relative flex h-2.5 w-2.5 shrink-0">
          {accent !== "quiet" && (
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${dot} opacity-60`} />
          )}
          <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${dot}`} />
        </span>
        <div className="min-w-0">
          <p className={`font-extrabold tracking-tight leading-tight ${compact ? "text-sm" : "text-[15px]"} ${headlineCl}`}>
            {headline}
          </p>
          {detail && (
            <p className="text-xs text-zinc-400 truncate mt-0.5">{detail}</p>
          )}
        </div>
      </div>
    </motion.div>
  );
}
